import React, { useState, useEffect } from 'react';
import designApi from '@services/designApi.jsx';
import ResourceUploadField from '@components/common/ResourceUploadField.jsx';

const CaptureStandbySetting = ({ themeId, themePageMaster }) => {
  // 촬영 대기 페이지 상태
  const [captureStandbyPage, setCaptureStandbyPage] = useState({
    themePageMaster: themePageMaster,
    languageCode: 'ko',
    guideText: '',
    guideTextColor: '#FFFFFF',
    timerSeconds: 10,
    timerTimeoutRedirectUrl: 'start'
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // API 호출로 데이터 가져오기
  useEffect(() => {
    const fetchCaptureStandbyPage = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await designApi.getCaptureStandbyPage(themeId);
        
        setCaptureStandbyPage({
          ...captureStandbyPage,
          ...data
        });

        console.log("API 호출 성공:", data);
      } catch (err) {
        console.error("API 호출 실패:", err);
        setError(err);
        // 에러 발생 시 기본 데이터 유지
      } finally {
        setIsLoading(false);
      }
    };

    fetchCaptureStandbyPage();
  }, [themeId]);

  // 입력값 변경 핸들러
  const handleInputChange = (field, value) => {
    setCaptureStandbyPage(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 리소스 업로드 처리 함수
  const handleResourceUploaded = (resourceType, resourceId) => {
    setCaptureStandbyPage({
      ...captureStandbyPage,
      [resourceType]: resourceId
    });
  };

  // 타이머 조절 핸들러
  const handleTimerDecrease = () => {
    setCaptureStandbyPage(prev => ({
      ...prev,
      timerSeconds: Math.max((prev.timerSeconds || 10) - 1, 1)
    }));
  };

  const handleTimerIncrease = () => {
    setCaptureStandbyPage(prev => ({
      ...prev,
      timerSeconds: (prev.timerSeconds || 10) + 1
    }));
  };

  // 저장 이벤트 리스너 (페이지 상위 컴포넌트에서 저장 요청 시 실행)
  useEffect(() => {
    const eventName = `savePageDesign_shooting_wait`; // 각 컴포넌트마다 고유한 이벤트 이름

    const saveHandler = async (event) => {
      try {
        // 저장 중임을 표시
        setIsLoading(true);

        // 자체 로직을 사용하여 데이터 저장
        const isSucceed = await designApi.saveCaptureStandbyPage(event.detail.themeId, captureStandbyPage);

        if(isSucceed) {
          // 성공 메시지
          alert("저장되었습니다.");
        } else {
          // 실패 메시지
          alert("저장에 실패했습니다.");
        }

      } catch (error) {
        // 오류 처리
        console.error("저장 실패:", error);
        alert("저장 중 오류가 발생했습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    // 이벤트 리스너 등록
    document.addEventListener(eventName, saveHandler);

    // 컴포넌트 언마운트 시 리스너 제거
    return () => {
      document.removeEventListener(eventName, saveHandler);
    };
  }, [captureStandbyPage, themeId]); // 의존성 배열에 필요한 상태 포함

  return (
    <div className="design-settings">
      {/* 언어 선택 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">언어 선택</h4>
        <div className="option-select-container">
          <select
            className="option-select"
            value={captureStandbyPage.languageCode || "ko"}
            onChange={(e) =>
              handleInputChange('languageCode', e.target.value)
            }
          >
            <option value="ko">한국어</option>
            <option value="en">영어</option>
            <option value="jp">일본어</option>
            <option value="cn">중국어</option>
          </select>
          <i className="ri-arrow-down-s-line select-arrow"></i>
        </div>
      </div>

      {/* 배경 이미지 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">배경 이미지</h4>
        <ResourceUploadField
          label=""
          resourceId={captureStandbyPage.backgroundResourceId}
          onResourceUploaded={(id) =>
            handleResourceUploaded("backgroundResourceId", id)
          }
          acceptTypes="image/*"
        />
      </div>

      {/* 배경 음악 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">배경 음악</h4>
        <ResourceUploadField
          label=""
          resourceId={captureStandbyPage.backgroundMusicResourceId}
          onResourceUploaded={(id) =>
            handleResourceUploaded("backgroundMusicResourceId", id)
          }
          acceptTypes="audio/*"
        />
      </div>

      {/* 문구 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">문구</h4>
        <div className="settings-row">
          <div className="settings-input">
            <input 
              type="text" 
              placeholder="문구를 입력해주세요" 
              className="text-input"
              value={captureStandbyPage.guideText || ""}
              onChange={(e) => handleInputChange('guideText', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={captureStandbyPage.guideTextColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={captureStandbyPage.guideTextColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('guideTextColor', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 사진 촬영 버튼 설정 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">사진 촬영 버튼</h4>
        <div className="settings-row">
          <div className="settings-label">사진 촬영 버튼</div>
          <ResourceUploadField
            label=""
            resourceId={captureStandbyPage.photoButtonDefaultResourceId}
            onResourceUploaded={(id) =>
              handleResourceUploaded("photoButtonDefaultResourceId", id)
            }
            acceptTypes="image/*"
          />
        </div>
        <div className="settings-row">
          <div className="settings-label">클릭 후 버튼</div>
          <ResourceUploadField
            label=""
            resourceId={captureStandbyPage.photoButtonActiveResourceId}
            onResourceUploaded={(id) =>
              handleResourceUploaded("photoButtonActiveResourceId", id)
            }
            acceptTypes="image/*"
          />
        </div>
      </div>

      {/* 동영상 촬영 버튼 설정 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">동영상 촬영 버튼</h4>
        <div className="settings-row">
          <div className="settings-label">동영상 촬영 버튼</div>
          <ResourceUploadField
            label=""
            resourceId={captureStandbyPage.videoButtonDefaultResourceId}
            onResourceUploaded={(id) =>
              handleResourceUploaded("videoButtonDefaultResourceId", id)
            }
            acceptTypes="image/*"
          />
        </div>
        <div className="settings-row">
          <div className="settings-label">클릭 후 버튼</div>
          <ResourceUploadField
            label=""
            resourceId={captureStandbyPage.videoButtonActiveResourceId}
            onResourceUploaded={(id) =>
              handleResourceUploaded("videoButtonActiveResourceId", id)
            }
            acceptTypes="image/*"
          />
        </div>
      </div>

      {/* 타이머 설정 섹션 */}
      {/* <div className="settings-box">
        <h4 className="settings-title">타이머</h4>
        <div className="settings-row">
          <div className="settings-label">타이머 시간</div>
          <div className="timer-control-container">
            <button 
              className="timer-btn" 
              onClick={handleTimerDecrease}
            >
              <i className="ri-arrow-down-s-fill"></i>
            </button>
            <input 
              type="text" 
              className="timer-input" 
              value={captureStandbyPage.timerSeconds || 10}
              onChange={(e) => handleInputChange('timerSeconds', parseInt(e.target.value) || 1)}
            />
            <button 
              className="timer-btn"
              onClick={handleTimerIncrease}
            >
              <i className="ri-arrow-up-s-fill"></i>
            </button>
            <span className="timer-unit">초</span>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">타임 오버 이동 페이지</div>
          <div className="page-select-container">
            <select 
              className="page-select"
              value={captureStandbyPage.timerTimeoutRedirectUrl || "start"}
              onChange={(e) => handleInputChange('timerTimeoutRedirectUrl', e.target.value)}
            >
              <option value="start">시작화면</option>
              <option value="angle">앵글선택</option>
              <option value="layout">레이아웃</option>
              <option value="payment">결제</option>
            </select>
            <i className="ri-arrow-down-s-line select-arrow"></i>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default CaptureStandbySetting;